import { Alert, Button, Card, Empty, Input, Space, Typography } from "antd";
import { type ReactNode, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router";
import { ZhMap, type ZhMapRef } from "zhihao-ui-react";

import { VNO_REGEXP } from "@/utils/regExp";
import { TruckLocationDetailPage } from "./TruckLocationDetailPage";
import type { TruckArchiveLoader, TruckLocationLoader, TruckTrackLoader } from "./types";

// 地图壳只负责提供稳定的初始视口，车辆定位和轨迹仍沿用现有页面业务逻辑。
const TRUCK_MAP_CENTER: [number, number] = [114.3055, 30.5928];
const TRUCK_MAP_ZOOM = 14;

export interface TruckLocationPageProps {
    permissions?: string[];
    loadVehicles?: TruckArchiveLoader;
    loadLocation?: TruckLocationLoader;
    loadTrack?: TruckTrackLoader;
}

/** 查车入口；保留 Vue 的车牌输入转大写和 ROLE_LF_CAR 权限控制。 */
export function TruckLocationPage({ permissions, loadVehicles, loadLocation, loadTrack }: TruckLocationPageProps) {
    const location = useLocation();
    const navigate = useNavigate();
    const initialVclN = new URLSearchParams(location.search).get("vclN") || "";
    const [vclN, setVclN] = useState(initialVclN);
    const [searchedVclN, setSearchedVclN] = useState(initialVclN);
    const [errorMessage, setErrorMessage] = useState("");
    const mapRef = useRef<ZhMapRef | null>(null);
    const canSearch = permissions === undefined || permissions.includes("ROLE_LF_CAR");

    if (!canSearch) return <Empty description="暂无查车权限" />;

    // 复用旧 Vue 页面“地图作为底层容器”的布局语义，不把地图能力扩展到查询/API 层。
    const renderMapShell = (content: ReactNode) => (
        <section aria-label="truck-location-map-shell" className="relative min-h-full overflow-hidden">
            <ZhMap
                center={TRUCK_MAP_CENTER}
                className="absolute inset-0 h-full min-h-[480px] w-full"
                layerType="vector"
                ref={mapRef}
                zoom={TRUCK_MAP_ZOOM}
            />
            <div className="relative z-10 p-4">{content}</div>
        </section>
    );

    if (searchedVclN) {
        return renderMapShell(
            <TruckLocationDetailPage
                vclN={searchedVclN}
                loadVehicles={loadVehicles}
                loadLocation={loadLocation}
                loadTrack={loadTrack}
                mapRef={mapRef}
            />
        );
    }

    const search = () => {
        const normalized = vclN.trim().toUpperCase();
        if (!normalized) return;
        if (!VNO_REGEXP.test(normalized)) {
            setErrorMessage("您输入的车牌号有误");
            return;
        }
        setErrorMessage("");
        setSearchedVclN(normalized);
        navigate(`/logistics/truckLocation?vclN=${encodeURIComponent(normalized)}`, { replace: true });
    };

    return renderMapShell(
        <section aria-label="truck-location-page" className="flex min-h-full flex-col gap-4">
            <Card>
                <Typography.Title className="mb-0" level={4}>
                    查车
                </Typography.Title>
            </Card>
            <Card>
                {errorMessage ? <Alert message={errorMessage} role="alert" type="error" /> : null}
                <Space.Compact className="w-full">
                    <Input
                        aria-label="车牌号"
                        placeholder="请输入车牌号搜索"
                        value={vclN}
                        onChange={event => setVclN(event.target.value.toUpperCase())}
                        onPressEnter={search}
                    />
                    <Button aria-label="搜索" type="primary" onClick={search}>
                        搜索
                    </Button>
                </Space.Compact>
                {!errorMessage ? (
                    <Typography.Text type="secondary">支持输入完整车牌号查询车辆档案</Typography.Text>
                ) : null}
            </Card>
        </section>
    );
}

export default TruckLocationPage;
